import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { Auth, User as FireAuthUser, signOut } from 'firebase/auth';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { ProfileImageDialogComponent } from 'src/app/modules/user-profile/profile/profile-image-dialog/profile-image-dialog.component';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { SidenavToggleService } from 'src/app/services/sidenav-toggle.service';
import { activeTheme } from 'src/main';
import { UserService } from '../../../services/user.service';
import { UserNotificationComponent } from '../../user-notification/user-notification.component';
import { Inject } from '@angular/core';
import { AUTH } from 'src/app/firebase-injection';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';

@Component({
  selector: 'app-header-toolbar',
  templateUrl: './header-toolbar.component.html',
  styleUrls: ['./header-toolbar.component.scss'],
  imports: [
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatMenuModule,
    MatToolbarModule,
    MatTooltipModule,
    UserNotificationComponent,
  ],
})
export class HeaderToolbarComponent implements OnInit, AfterViewInit {
  @Input() title: string | undefined = '';
  @Input() goBackButtonUrl: string | undefined;
  @Input() backButtonEnabled = true;
  @Input() subtitle: string | undefined;
  @Input() showUserProfileButton = true;
  @Input() menuButtonDisabled = false;
  @Input() userMenuDisabled = false;
  @Input() checkBeforeNavigation = () => Promise.resolve(true);
  @Output() navigationStopped: EventEmitter<void> = new EventEmitter<void>();
  @Output() goBackButtonClick: EventEmitter<void> = new EventEmitter<void>();
  @Output() menuButtonClick: EventEmitter<void> = new EventEmitter<void>();
  currentFireAuthUser: FireAuthUser | undefined = undefined;
  userIconCssProperty: string | undefined;
  isDesktop = true;
  currentLanguage: 'en' | 'de' = 'en';

  constructor(
    @Inject(AUTH) private auth: Auth,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private localStorage: LocalStorageService,
    private sidenavToggleService: SidenavToggleService,
    private userService: UserService,
    private dialog: MatDialog,
    private breakpointObserver: BreakpointObserver
  ) {}

  isUserProfilePage: boolean = false;

  ngOnInit(): void {
    const storedUser = this.localStorage.getUser();
    this.userIconCssProperty = storedUser?.photoURL
      ? `url(${storedUser.photoURL})`
      : 'url(assets/account_circle.svg)';
    this.currentFireAuthUser = this.auth.currentUser || undefined;

    this.breakpointObserver.observe([Breakpoints.Web]).subscribe((state) => {
      this.isDesktop = state.matches;
    });
    // Initial direkt setzen
    this.isUserProfilePage = this.router.url.startsWith('/app/user-profile');

    // Damit Angular das *ngIf sofort richtig rendert:

    // Bei Navigation aktualisieren
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.isUserProfilePage =
          event.urlAfterRedirects.startsWith('/app/user-profile');
      }
    });

    // Fallback to the auth image only if nothing from storage is available
    if (!storedUser?.photoURL) {
      this.userIconCssProperty = this.auth.currentUser?.photoURL
        ? `url(${this.auth.currentUser.photoURL})`
        : 'url(assets/account_circle.svg)';
    }

    if (this.auth.currentUser?.uid) {
      this.userService.getUser(this.auth.currentUser.uid).then((user) => {
        this.userIconCssProperty = user?.photoURL
          ? `url(${user.photoURL})`
          : 'url(assets/account_circle.svg)';
      });
    }
  }

  ngAfterViewInit() {
    const highlightText = $localize`:@@sentryHighlight:Highlight`;
    const hideText = $localize`:@@Hide:Hide`;
    const feedback = Sentry.getFeedback();
    const reportBugButton = document.querySelector('#reportBug');
    if (reportBugButton) {
      feedback?.attachTo(reportBugButton);

      let observer: MutationObserver | null = null;

      reportBugButton.addEventListener('click', () => {
        setTimeout(() => {
          const sentryFeedback = document.querySelector('#sentry-feedback');
          if (sentryFeedback?.shadowRoot) {
            if (
              !sentryFeedback.shadowRoot.querySelector('style[data-custom]')
            ) {
              const style = document.createElement('style');
              style.setAttribute('data-custom', 'true');
              style.textContent = `
                .btn.btn--default {
                  background: ${activeTheme.alternativeBackground} !important;
                  color: white !important;
                  border: none !important;
                }
              `;
              sentryFeedback.shadowRoot.appendChild(style);
            }

            if (!observer) {
              observer = new MutationObserver((mutations, obs) => {
                // required to override the default text of the buttons used in the screenshot editor
                let found = false;
                sentryFeedback.shadowRoot
                  ?.querySelectorAll('.editor button, .editor span')
                  .forEach((el) => {
                    if (el.textContent?.trim() === 'Highlight') {
                      el.textContent = highlightText;
                      found = true;
                    }
                    if (el.textContent?.trim() === 'Hide') {
                      el.textContent = hideText;
                      found = true;
                    }
                  });
                if (found) {
                  obs.disconnect();
                  observer = null;
                }
              });

              observer.observe(sentryFeedback.shadowRoot, {
                childList: true,
                subtree: true,
              });
            }
          }
        }, 100);
      });
    }
  }

  public logout() {
    this.localStorage.clear();
    signOut(this.auth)
      .then(() => {
        this.router.navigate(['/landing']);
      })
      .catch((error) => {
        console.error(error);
      });
  }

  async goBackButtonClicked() {
    this.goBackButtonClick.emit();
    if (await this.checkBeforeNavigation()) {
      this.router.navigate([this.goBackButtonUrl || '/app']);
    } else {
      this.navigationStopped.emit();
    }
  }

  async menuButtonClicked() {
    if (await this.checkBeforeNavigation()) {
      this.sidenavToggleService.open();
      this.menuButtonClick.emit();
    } else {
      this.navigationStopped.emit();
      this.sidenavToggleService.open();
    }
  }

  userIconClicked(event?: MouseEvent) {
    const currentUrl = this.router.url;

    if (currentUrl === '/app/user-profile/profile') {
      event?.stopPropagation(); // verhindert Öffnen des Menüs
      this.dialog.open(ProfileImageDialogComponent, {
        panelClass: 'profile-image-dialog',
      });
    }
  }

  navigateToUserProfile(): void {
    this.router.navigate(['/app/user-profile/profile']);
  }

  handleUserIconClick(trigger: MatMenuTrigger) {
    if (this.router.url === '/app/user-profile/profile') {
      this.dialog.open(ProfileImageDialogComponent, {
        panelClass: 'profile-image-dialog',
      });
    } else {
      trigger.openMenu(); // <-- korrektes Öffnen des Menüs
    }
  }

  updateUserIcon(photoURL: string | null) {
    this.userIconCssProperty = photoURL
      ? `url(${photoURL})`
      : 'url(assets/account_circle.svg)';
  }
}
